import { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import moment from "moment";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import {
    CalendarIcon24,
    ClockIcon24,
    UsersIcon24,
    PlusIcon24,
    BulletPointIcon,
} from "@/components/icons";

import { Client } from "@/enums/clients";

import { getTimeDifference } from "@/utils/get-time-difference";
import useQueryParams from "@/utils/use-query-params";

import type { UserInfo } from "../components/crew-cognitive-load/crew-cognitive-load.types";
import {
    CrewWrapper,
    DateDiv,
    DateAndTimeWrapper,
    DurationDiv,
    NameAndIconDiv,
    TimeDiv,
    TraineeNameSpan,
    UserAndSessionInfoWrapper,
    CrewsAndIconWrapper,
    UnitsWrapper,
    CrewsWrapper,
    IndividualCrewWrapper,
    GlobalWrapper,
    UserLabel,
    RemainingCrewsWrapper,
    ViewMoreWrapper,
    DateTimeAndDurationWrapper,
} from "../session-detail.styles";

interface UseSessionInfoProps {
    selectedCrewOption?: string;
    isInsideModal?: boolean;
}

type CrewMember = string;
type Crew = Record<string, CrewMember[]>;

export const useSessionInfo = ({ selectedCrewOption }: UseSessionInfoProps = {}) => {
    const { state }: { state: Record<string, string> } = useLocation();

    const intl = useIntl();
    const params: any = useQueryParams();
    const { customerName } = useSessionDetailsContext();

    const transitionRef = useRef(null);

    const [isViewMoreClicked, setIsViewMoreClicked] = useState<boolean>(false);

    const sessionPeriodFrom = params.get("from");
    const sessionPeriodTo = params.get("to");
    const name = params.get("name");
    const simulationName = params.get("simulationName");
    const traineeName = params.get("traineeName");
    const logoUrl = params.get("logoUrl");

    const sessionDuration = getTimeDifference(
        new Date(sessionPeriodFrom),
        new Date(sessionPeriodTo),
    );

    const getUsersInfo = () => {
        const cacheUsers = localStorage.getItem("users");

        if (cacheUsers !== null) {
            const parseUsers = JSON.parse(cacheUsers);

            return parseUsers;
        }
    };

    const usersInfo = getUsersInfo();

    const getUserNames = () => {
        const cacheUsers = localStorage.getItem("users"); //why do we need to get the users from localStorage??

        if (cacheUsers !== null) {
            const parseUsers = JSON.parse(cacheUsers);

            const usersArray = parseUsers.map((user: any) => {
                return user.name;
            });

            return usersArray;
        }

        return [state?.traineeName || traineeName]; // Return the single user in an array
    };

    const users = getUserNames();

    const hasNoUsers =
        !users ||
        users?.every((user: string) => {
            return !user?.trim() || user?.trim() === "undefined";
        });

    const handleViewMoreClick = () => {
        setIsViewMoreClicked((prevState) => !prevState);
    };

    //1 user
    const isSingleUser = users.length === 1;
    //3 users
    const isCrew = users.length > 1 && users.length <= 3;
    //more than 3 users
    const isUnit = users.length > 3;

    const groupedCrews: Crew = users.reduce(
        (acc: Record<string, UserInfo[]>, user: UserInfo, index: number) => {
            const crewNumber = Math.floor(index / 3) + 1; //group size of 3
            const crewName = `crew${crewNumber}`;

            acc[crewName] = [...(acc[crewName] || []), user];

            return acc;
        },
        {},
    );

    const firstCrew = Object.values(groupedCrews || [])[0];
    const remainingCrews = Object.values(groupedCrews || []).slice(1);

    const selectedCrew = groupedCrews[selectedCrewOption || ""];

    let title = useMemo(() => {
        const sessionTitle = name;

        if (!sessionTitle) {
            return `${state?.simulationName || simulationName || ""}`;
        }

        return `${state?.simulationName || simulationName || ""} \u2022 ${sessionTitle}`;
    }, [state?.simulationName, simulationName, name, intl]);

    if (customerName == Client.MLRS) {
        title = `${"TCT - MLRS"} \u2022 ${"Detachment Commanders Course"}`;
    }

    const getSessionSubtitleDetails = useMemo(() => {
        const sessionDate = moment(sessionPeriodFrom || sessionPeriodTo).format("DD/MM/YYYY");
        const fromTime = moment(sessionPeriodFrom).format("HH:mm");

        return (
            <GlobalWrapper>
                {isUnit && (
                    <>
                        <CrewsAndIconWrapper>
                            <UnitsWrapper>
                                <CrewsWrapper>
                                    <IndividualCrewWrapper>
                                        {firstCrew.map((user: string, index: number) => {
                                            return (
                                                <UserLabel key={`${user}-${index}`}>
                                                    <UsersIcon24 fill="#999999" />
                                                    {user}
                                                </UserLabel>
                                            );
                                        })}
                                    </IndividualCrewWrapper>

                                    <CSSTransition
                                        in={isViewMoreClicked}
                                        timeout={200}
                                        classNames="fade-slide"
                                        unmountOnExit
                                        nodeRef={transitionRef}
                                    >
                                        <RemainingCrewsWrapper ref={transitionRef}>
                                            {remainingCrews.map(
                                                (remainingCrew: string[], index: number) => {
                                                    return (
                                                        <IndividualCrewWrapper
                                                            key={`remaining-crew-${index}`}
                                                        >
                                                            {remainingCrew.map(
                                                                (user: string, index: number) => {
                                                                    return (
                                                                        <UserLabel
                                                                            key={`${user}-${index}`}
                                                                        >
                                                                            <UsersIcon24 fill="#999999" />
                                                                            {user}
                                                                        </UserLabel>
                                                                    );
                                                                },
                                                            )}
                                                        </IndividualCrewWrapper>
                                                    );
                                                },
                                            )}
                                        </RemainingCrewsWrapper>
                                    </CSSTransition>
                                </CrewsWrapper>
                            </UnitsWrapper>

                            <ViewMoreWrapper onClick={handleViewMoreClick}>
                                <PlusIcon24 />
                                {isViewMoreClicked ? "View Less" : "View More"}
                            </ViewMoreWrapper>
                        </CrewsAndIconWrapper>

                        <DateTimeAndDurationWrapper>
                            <DateAndTimeWrapper>
                                <DateDiv>
                                    <CalendarIcon24 />
                                    {sessionDate}
                                </DateDiv>

                                <TimeDiv>
                                    <ClockIcon24 />
                                    {fromTime}
                                </TimeDiv>
                            </DateAndTimeWrapper>

                            <BulletPointIcon fill="#999999" />

                            <DurationDiv>{sessionDuration}</DurationDiv>
                        </DateTimeAndDurationWrapper>
                    </>
                )}

                {isSingleUser && (
                    <UserAndSessionInfoWrapper>
                        {!hasNoUsers &&
                            users?.map((user: any, index: number) => {
                                return (
                                    <NameAndIconDiv key={`${index}-${user}`}>
                                        <UsersIcon24 fill="#999999" />
                                        <TraineeNameSpan>{user}</TraineeNameSpan>
                                    </NameAndIconDiv>
                                );
                            })}
                        <DateDiv>
                            <CalendarIcon24 />
                            {sessionDate}
                        </DateDiv>

                        <TimeDiv>
                            <ClockIcon24 />
                            {fromTime}
                        </TimeDiv>

                        <BulletPointIcon fill="#999999" />

                        <DurationDiv>{sessionDuration}</DurationDiv>
                    </UserAndSessionInfoWrapper>
                )}

                {isCrew && (
                    <CrewWrapper>
                        <UserAndSessionInfoWrapper>
                            {!hasNoUsers &&
                                users?.map((user: any, index: number) => {
                                    return (
                                        <NameAndIconDiv key={`${index}-${user}`}>
                                            <UsersIcon24 fill="#999999" />
                                            <TraineeNameSpan>{user}</TraineeNameSpan>
                                        </NameAndIconDiv>
                                    );
                                })}
                        </UserAndSessionInfoWrapper>

                        <DateTimeAndDurationWrapper>
                            <DateDiv>
                                <CalendarIcon24 />
                                {sessionDate}
                            </DateDiv>

                            <TimeDiv>
                                <ClockIcon24 />
                                {fromTime}
                            </TimeDiv>

                            <BulletPointIcon fill="#999999" />

                            <DurationDiv>{sessionDuration}</DurationDiv>
                        </DateTimeAndDurationWrapper>
                    </CrewWrapper>
                )}
            </GlobalWrapper>
        );
    }, [
        sessionPeriodFrom,
        sessionPeriodTo,
        state?.traineeName,
        traineeName,
        sessionDuration,
        isViewMoreClicked,
    ]);

    const getModalSubtitleDetails = useMemo(() => {
        const sessionDate = moment(sessionPeriodFrom || sessionPeriodTo).format("DD/MM/YYYY");
        const fromTime = moment(sessionPeriodFrom).format("HH:mm");

        return (
            <GlobalWrapper>
                <CrewWrapper>
                    <UserAndSessionInfoWrapper>
                        {(selectedCrew || []).map((user: string, index: number) => {
                            return (
                                <NameAndIconDiv key={`${index}-${user}`}>
                                    <UsersIcon24 fill="#999999" />
                                    <TraineeNameSpan>{user}</TraineeNameSpan>
                                </NameAndIconDiv>
                            );
                        })}
                    </UserAndSessionInfoWrapper>

                    <DateAndTimeWrapper>
                        <DateDiv>
                            <CalendarIcon24 />
                            {sessionDate}
                        </DateDiv>

                        <TimeDiv>
                            <ClockIcon24 />
                            {fromTime}
                        </TimeDiv>
                    </DateAndTimeWrapper>
                </CrewWrapper>
            </GlobalWrapper>
        );
    }, [
        sessionPeriodFrom,
        sessionPeriodTo,
        state?.traineeName,
        traineeName,
        sessionDuration,
        selectedCrew,
    ]);

    return {
        getSessionSubtitleDetails,
        getModalSubtitleDetails,
        logoUrl,
        title,
        state,
        usersInfo,
        users,
    };
};
